/**
 * Conflict detection and split/merge helpers for Plan scheduling.
 *
 * Busy intervals (appointments, blockers) and other time blocks both count as
 * occupied time. Segments of the same task are excluded when checking whether a
 * move can restore a single uninterrupted block.
 */

import { SNAP_MINUTES } from '@/lib/motion';
import type { EventDropArg } from '@fullcalendar/core';
import type { EventResizeDoneArg } from '@fullcalendar/interaction';
import type { BusyInterval, TimeBlock } from './types';

type DropArg = EventDropArg | EventResizeDoneArg;

/** Start/end after a drag or resize, preserving duration when FC omits `end`. */
export function getDroppedRange(
  arg: DropArg,
  fallbackDurationMs?: number,
): { start: Date; end: Date } | null {
  const start = arg.event.start;
  if (!start) return null;

  let end = arg.event.end;
  if (!end) {
    const oldStart = arg.oldEvent.start;
    const oldEnd = arg.oldEvent.end;
    if (oldStart && oldEnd) {
      end = new Date(start.getTime() + (oldEnd.getTime() - oldStart.getTime()));
    } else if (fallbackDurationMs != null) {
      end = new Date(start.getTime() + fallbackDurationMs);
    } else {
      return null;
    }
  }

  return { start, end };
}

export interface MsInterval {
  starts_at: number;
  ends_at: number;
}

export interface BlockSegment {
  startsAt: Date;
  endsAt: Date;
  isContinuation: boolean;
}

const ACTIVE_BLOCK: TimeBlock['status'][] = ['proposed', 'confirmed'];

export function isActiveBlock(status: TimeBlock['status']): boolean {
  return ACTIVE_BLOCK.includes(status);
}

export function overlapsMs(
  aStart: number,
  aEnd: number,
  bStart: number,
  bEnd: number,
): boolean {
  return aStart < bEnd && bStart < aEnd;
}

export function mergeIntervals(intervals: MsInterval[]): MsInterval[] {
  if (intervals.length === 0) return [];
  const ordered = [...intervals].sort((a, b) => a.starts_at - b.starts_at);
  const merged: MsInterval[] = [{ starts_at: ordered[0]!.starts_at, ends_at: ordered[0]!.ends_at }];
  for (let i = 1; i < ordered.length; i += 1) {
    const cur = ordered[i]!;
    const last = merged[merged.length - 1]!;
    if (cur.starts_at <= last.ends_at) {
      last.ends_at = Math.max(last.ends_at, cur.ends_at);
    } else {
      merged.push({ starts_at: cur.starts_at, ends_at: cur.ends_at });
    }
  }
  return merged;
}

export function busyToMs(busy: BusyInterval[]): MsInterval[] {
  return busy.map((b) => ({
    starts_at: new Date(b.starts_at).getTime(),
    ends_at: new Date(b.ends_at).getTime(),
  }));
}

export function blocksToMs(
  blocks: TimeBlock[],
  exclude: { blockId?: number; taskId?: number } = {},
): MsInterval[] {
  return blocks
    .filter(
      (b) =>
        isActiveBlock(b.status) &&
        b.id !== exclude.blockId &&
        (exclude.taskId == null || b.task_id !== exclude.taskId),
    )
    .map((b) => ({
      starts_at: new Date(b.starts_at).getTime(),
      ends_at: new Date(b.ends_at).getTime(),
    }));
}

/** All occupied time that should block a new placement or move. */
export function buildOccupied(
  busy: BusyInterval[],
  blocks: TimeBlock[],
  exclude: { blockId?: number; taskId?: number } = {},
): MsInterval[] {
  return mergeIntervals([...busyToMs(busy), ...blocksToMs(blocks, exclude)]);
}

export function intervalConflicts(
  start: Date,
  end: Date,
  occupied: MsInterval[],
): boolean {
  const s = start.getTime();
  const e = end.getTime();
  return occupied.some((o) => overlapsMs(s, e, o.starts_at, o.ends_at));
}

const MIN_SEGMENT_MS = SNAP_MINUTES * 60_000;

/**
 * Split a desired duration around occupied intervals.
 *
 * Returns null when splitting is not possible or would not produce multiple
 * segments (caller should offer overlap only).
 */
export function splitAroundOccupied(
  start: Date,
  end: Date,
  occupied: MsInterval[],
  options: { searchEnd?: Date; minSegmentMs?: number } = {},
): BlockSegment[] | null {
  const startMs = start.getTime();
  const totalMs = end.getTime() - startMs;
  if (totalMs < MIN_SEGMENT_MS) return null;

  const minSegmentMs = options.minSegmentMs ?? MIN_SEGMENT_MS;
  const searchEndMs =
    options.searchEnd?.getTime() ?? startMs + totalMs + 24 * 60 * 60_000;

  const merged = mergeIntervals(
    occupied.filter((o) => o.ends_at > startMs && o.starts_at < searchEndMs),
  );

  const segments: BlockSegment[] = [];
  let cursor = startMs;
  let remaining = totalMs;

  while (remaining > 0) {
    // Skip busy intervals covering the cursor.
    let skipped = false;
    for (const busy of merged) {
      if (busy.starts_at <= cursor && busy.ends_at > cursor) {
        cursor = busy.ends_at;
        skipped = true;
      }
    }
    if (skipped) continue;

    if (cursor >= searchEndMs) break;

    const nextBusy = merged.find((o) => o.starts_at > cursor);
    const freeEnd = nextBusy ? nextBusy.starts_at : searchEndMs;
    const slotMs = freeEnd - cursor;

    if (slotMs < minSegmentMs) {
      if (nextBusy) {
        cursor = nextBusy.ends_at;
        continue;
      }
      break;
    }

    const take = Math.min(remaining, slotMs);
    if (take < minSegmentMs) {
      if (nextBusy) {
        cursor = nextBusy.ends_at;
        continue;
      }
      break;
    }

    segments.push({
      startsAt: new Date(cursor),
      endsAt: new Date(cursor + take),
      isContinuation: segments.length > 0,
    });
    remaining -= take;
    cursor += take;
  }

  if (remaining > 0 || segments.length < 2) return null;
  return segments;
}

/** Sum active block durations for one task. */
export function totalBlockDurationMs(blocks: TimeBlock[], taskId: number): number {
  return blocks
    .filter((b) => b.task_id === taskId && isActiveBlock(b.status))
    .reduce(
      (sum, b) =>
        sum + (new Date(b.ends_at).getTime() - new Date(b.starts_at).getTime()),
      0,
    );
}

/** True when [start, start + totalMs) is entirely free. */
export function canRestoreSingleBlock(
  start: Date,
  totalMs: number,
  occupied: MsInterval[],
): boolean {
  if (totalMs < MIN_SEGMENT_MS) return false;
  const s = start.getTime();
  const e = s + totalMs;
  return !occupied.some((o) => overlapsMs(s, e, o.starts_at, o.ends_at));
}

export function blockTitle(
  block: Pick<TimeBlock, 'task_title' | 'is_continuation'>,
  opts: { late?: boolean } = {},
): string {
  const base = block.is_continuation ? `cont ${block.task_title}` : block.task_title;
  if (opts.late) return `${base} · past due`;
  return base;
}

export function newSplitGroupId(): string {
  return crypto.randomUUID();
}
